import i18n, { parseFloatLocale } from '@/helper/Translations'
import { defineRule } from '@/helper/ValidateDirective'
import { FromValidationRuleType, ValidationRuleType } from '@/helper/Types'

export function setUpValidatorRules() {
  const numericRegex = /^-?\d+(?:[,.]\d+(?:[,.]\d+)?)?$/m

  defineRule(ValidationRuleType.REQUIRED, (value: string | undefined | string[]) => {
    if (
      value === undefined ||
      value === null ||
      (Array.isArray(value) && value.length == 0) ||
      (typeof value === 'string' && value.trim().length == 0)
    ) {
      return i18n.global.t('validation.required').toString()
    }
    return true
  })

  defineRule(ValidationRuleType.EMAIL, (value: string | undefined) => {
    if (value === undefined || value === null || value.trim().length == 0) return true
    // eslint-disable-next-line
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(value)) ? true : i18n.global.t('validation.email').toString()
  })

  defineRule(ValidationRuleType.TRANSLATION_KEY, (value: string | undefined) => {
    if (value === undefined || value === null || value.trim().length == 0) return true
    // eslint-disable-next-line
    const re = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/i // e.g. foo.bar -> at least two items separated by dot
    return re.test(String(value)) ? true : 'Please provide a valid key.'
  })

  defineRule(ValidationRuleType.INTEGER, (value: string | undefined) => {
    if ((value || '').toString().replace(/\s/g, '').trim() === '') return true
    return /^-?[0-9]+$/.test(String(value).replace(/\s/g, '')) ? true : i18n.global.t('validation.integer').toString()
  })

  defineRule(ValidationRuleType.MIN_VALUE, (value: string | undefined, params: string[]) => {
    const min = params[0]
    if (value === undefined || value === null || value === '') return true
    return Number(value) >= Number(min) ? true : i18n.global.t('validation.min', { min: min }).toString()
  })

  defineRule(ValidationRuleType.MAX_VALUE, (value: string | undefined, params: string[]) => {
    const max = parseFloat(params[0])
    if (value === undefined || value === null || value === '') return true
    return parseFloat(value.replace(/,/g, '.')) <= max ? true : i18n.global.t('validation.max_value', { max: max }).toString()
  })

  defineRule(ValidationRuleType.ONE_OF, (value: string | undefined, params: string[]) => {
    if ((value || '').toString().trim() === '') return true
    return Array.from(params).some(item => item == value) ? true : i18n.global.t('validation.required').toString()
  })

  defineRule(ValidationRuleType.STARTS_WITH, (value: string | undefined, params: string[]) => {
    if ((value || '').toString().trim() === '') return true
    return Array.from(params).some(item => (value?.indexOf(item) === 0 ? value.substr(item.length, 1) === '.' : false))
      ? true
      : 'This key is not allowed.'
  })

  defineRule(ValidationRuleType.MAX, (value: string | undefined, params: string[]) => {
    const length = params[0]
    if ((value || '').toString().trim() === '') return true
    return String(value).length <= Number(length)
      ? true
      : i18n.global.t('validation.max', { length: length }).toString()
  })

  defineRule(ValidationRuleType.POSITIVE_DECIMAL, (value: string | undefined) => {
    if ((value || '').toString().trim() === '') return true
    if ((value || '').toString().replace(/\s/g, '').match(numericRegex) === null)
      return i18n.global.t('validation.positiveDecimal').toString()
    const parsedNumber: number = parseFloat(
      parseFloatLocale(value || '')
        .toString()
        .replace(',', '.')
    )
    return !isNaN(parsedNumber) && parsedNumber > 0 ? true : i18n.global.t('validation.positiveDecimal').toString()
  })

  defineRule(ValidationRuleType.POSITIVE_ZERO_DECIMAL, (value: string | undefined) => {
    if ((value || '').toString().trim() === '') return true
    if ((value || '').toString().replace(/\s/g, '').match(numericRegex) === null)
      return i18n.global.t('validation.positiveZeroDecimal').toString()
    const parsedNumber: number = parseFloat((value || '').toString().replace(',', '.'))
    return !isNaN(parsedNumber) && parsedNumber >= 0 ? true : i18n.global.t('validation.positiveZeroDecimal').toString()
  })

  defineRule(ValidationRuleType.DECIMAL, (value: string | undefined) => {
    if ((value || '').toString().trim() === '') return true
    if ((value || '').toString().replace(/\s/g, '').match(numericRegex) === null)
      return i18n.global.t('validation.decimal').toString()
    const parsedNumber: number = parseFloat(
      parseFloatLocale(value || '')
        .toString()
        .replace(',', '.')
    )
    return !isNaN(parsedNumber) ? true : i18n.global.t('validation.decimal').toString()
  })

  defineRule(ValidationRuleType.UNIQUE, (value: string | undefined, params: string[]) => {
    if ((value || '').toString().trim() === '') return true
    const para = params as any as { list: any[]; key: string }
    const amount = (para.key === null ? para.list : para.list.map(e => e[para.key])).reduce(
      (prev, e) => prev + (e === value ? 1 : 0),
      0
    )
    return amount > 1 ? 'List value has to be unique.' : true
  })

  defineRule(ValidationRuleType.VALID_POSTAL_CODE, (value: string | undefined, params: Record<string, any>) => {
    const isInactive = !params.isActive
    const isIndexValid = params.availableCodes().indexOf(value) >= 0
    const isRegexMatched = params.regexp() ? new RegExp(params.regexp()).test((value || '').toString()) : true
    const isBlank = (value || '').trim() === ''

    if ((isInactive || isIndexValid) && (isRegexMatched || isBlank)) {
      return true
    } else {
      return i18n.global.t('validation.non_valid_postal_code').toString()
    }
  })
}

export function containsRuleRequired(rules: FromValidationRuleType) {
  rules = rules || ''
  if (typeof rules === 'function') {
    rules = (rules as () => Record<string, unknown>)()
  }
  rules = typeof rules === 'string' ? rules.split('|') : rules
  return Array.isArray(rules) ? rules.includes('required') : 'required' in rules
}
